// Kit 01 · item 09.  Kit 02 · item 47.
//
//   09 Segmented control   track pad 4 · segment pad 4 by 12 · radius 8/6
//   47 Sub tabs, pill      track padding 4 · pill 4/12 · radius 6 in 8 · gap 4
//
// keys: Arrow keys move between options. Space or Enter selects. ONE tab stop
//       for the whole control — a five-segment control that is five tab stops
//       is five times as far to walk past.
//
// The two items are the same object with different rest ink, so they are one
// component with a variant rather than two that will drift.

import { useRef } from "react";
import { cx, stateAttr } from "./kit-state";
import type { KitState, Stateful } from "./kit-state";

export interface SegmentedOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Force this one segment's state. Gallery only. */
  state?: KitState;
}

export interface SegmentedProps extends Stateful {
  options: SegmentedOption[];
  value: string;
  onChange?: (value: string) => void;
  /** "control" is item 09, "tabs" is item 47. */
  variant?: "control" | "tabs";
  /** The accessible name for the group. Required: a bare row of pills says
      nothing about what it switches. */
  label: string;
  className?: string;
}

export function Segmented({
  options,
  value,
  onChange,
  variant = "control",
  label,
  state,
  className,
}: SegmentedProps) {
  const track = useRef<HTMLDivElement | null>(null);

  // Roving focus. The selected option is the only tab stop; arrows move the
  // selection and the focus together, which is what makes one tab stop
  // navigable rather than merely short.
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const usable = options.filter((o) => !o.disabled);
    if (usable.length === 0) return;
    const at = usable.findIndex((o) => o.value === value);
    let next = at;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = (at + 1) % usable.length;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = (at - 1 + usable.length) % usable.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = usable.length - 1;
    else return;
    event.preventDefault();
    const target = usable[next];
    onChange?.(target.value);
    const el = track.current?.querySelector<HTMLButtonElement>(`[data-value="${target.value}"]`);
    el?.focus();
  }

  const groupDisabled = state === "disabled";

  return (
    <div
      ref={track}
      className={cx("k-seg", variant === "tabs" && "k-seg--tabs", className)}
      role={variant === "tabs" ? "tablist" : "radiogroup"}
      aria-label={label}
      onKeyDown={onKeyDown}
      {...stateAttr(state)}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            data-value={option.value}
            className="k-seg__item"
            role={variant === "tabs" ? "tab" : "radio"}
            aria-selected={variant === "tabs" ? selected : undefined}
            aria-checked={variant === "tabs" ? undefined : selected}
            // One tab stop: everything but the selected option is skipped.
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled || groupDisabled}
            onClick={() => onChange?.(option.value)}
            {...stateAttr(option.state ?? (selected ? "selected" : undefined))}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
